import React, { useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import { CalendarIcon } from 'lucide-react';

const EquipmentTab = ({
  register,
  errors,
  watch,
  setValue,
  inputClassName,
  equipmentTypes,
  equipmentManufacturers
}) => {

  const [showEquipmentModal, setShowEquipmentModal] = useState(false);

  const [equipment, setEquipment] = useState([]);

  const [newEquipment, setNewEquipment] = useState({
    equipmentType: '',
    manufacturer: '',
    model: '',
    sku: '',
    serialNumber: '',
    equipmentLocation: '',
    installationDate: '',
    warrantyExpires: '',
    notes: '',
    serviceLocation: '',
    extendedWarranty: false
  });

  // Options for select fields
  const equipmentTypeOptions = equipmentTypes.map(type => ({
    value: type.id,
    label: type.name
  }));

  const manufacturerOptions = equipmentManufacturers.map(manufacturer => ({
    value: manufacturer.id,
    label: manufacturer.name
  }));

  const serviceLocationOptions = [
    {
      value: '1061_west_geneva',
      label: '1061 West Geneva Drive - Temple'
    }
  ];

  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: '#D1D5DB',
      '&:hover': { borderColor: '#009efb' },
      boxShadow: 'none',
      '&:focus-within': {
        borderColor: '#009efb',
        boxShadow: '0 0 0 1px #009efb'
      }
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#009efb'
        : state.isFocused
        ? '#f3f4f6'
        : 'white',
      color: state.isSelected ? 'white' : '#374151',
      '&:hover': {
        backgroundColor: state.isSelected
          ? '#009efb'
          : '#f3f4f6'
      }
    })
  };

  const handleAddEquipment = () => {
    if (newEquipment.equipmentType && newEquipment.model) {

      setEquipment([
        ...equipment,
        {
          ...newEquipment,
          id: Date.now()
        }
      ]);

      setNewEquipment({
        equipmentType: '',
        manufacturer: '',
        model: '',
        sku: '',
        serialNumber: '',
        equipmentLocation: '',
        installationDate: '',
        warrantyExpires: '',
        notes: '',
        serviceLocation: '',
        extendedWarranty: false
      });

      setShowEquipmentModal(false);
    }
  };

  const removeEquipment = (id) => {
    setEquipment(
      equipment.filter(item => item.id !== id)
    );
  };

  return (
    <div className="space-y-8">

      {/* Equipment List */}
      <div className="bg-gray-50 p-6 rounded-lg">

        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Customer Equipment
          </h3>

          <button
            type="button"
            onClick={() => setShowEquipmentModal(true)}
            className="flex items-center px-3 py-1 text-sm text-[#009efb] hover:text-[#0089db]"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add New
          </button>
        </div>

        {equipment.length === 0 ? (

          <p className="text-gray-500 text-center py-4">
            No equipment added yet
          </p>

        ) : (

          <div className="space-y-4">

            {equipment.map((item) => (

              <div
                key={item.id}
                className="border border-gray-200 rounded-lg p-4"
              >

                <div className="flex justify-between items-start">

                  <div className="flex-1">

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                      <div>
                        <h4 className="font-medium text-gray-900">
                          {item.model}
                        </h4>

                        <p className="text-sm text-gray-600">
                          {
                            equipmentTypeOptions.find(
                              opt => opt.value === item.equipmentType
                            )?.label
                          }
                          {' - '}
                          {
                            manufacturerOptions.find(
                              opt => opt.value === item.manufacturer
                            )?.label
                          }
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">SKU:</span>{' '}
                          {item.sku || 'N/A'}
                        </p>

                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Serial:</span>{' '}
                          {item.serialNumber || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">
                            Installation:
                          </span>{' '}
                          {item.installationDate || 'N/A'}
                        </p>

                        <p className="text-sm text-gray-600">
                          <span className="font-medium">
                            Warranty:
                          </span>{' '}
                          {item.warrantyExpires || 'N/A'}
                        </p>
                      </div>

                    </div>

                    {item.equipmentLocation && (
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">
                          Location:
                        </span>{' '}
                        {item.equipmentLocation}
                      </p>
                    )}

                    {item.notes && (
                      <p className="text-sm text-gray-600 mt-2">
                        <span className="font-medium">
                          Notes:
                        </span>{' '}
                        {item.notes}
                      </p>
                    )}

                  </div>

                  <button
                    type="button"
                    onClick={() => removeEquipment(item.id)}
                    className="text-red-600 hover:text-red-800 ml-4"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>

                </div>

              </div>

            ))}

          </div>

        )}

      </div>

      {/* Add Equipment Modal */}
      {showEquipmentModal && (

        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">

          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">

            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Add New Customer Equipment
            </h3>

            <div className="space-y-4">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Equipment Type *
                  </label>

                  <Select
                    options={equipmentTypeOptions}
                    value={equipmentTypeOptions.find(
                      option =>
                        option.value === newEquipment.equipmentType
                    )}
                    onChange={(option) =>
                      setNewEquipment({
                        ...newEquipment,
                        equipmentType: option?.value || ''
                      })
                    }
                    styles={customStyles}
                    placeholder="Select equipment type"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manufacturer
                  </label>

                  <Select
                    options={manufacturerOptions}
                    value={manufacturerOptions.find(
                      option =>
                        option.value === newEquipment.manufacturer
                    )}
                     
                    styles={customStyles}
                    placeholder="Select manufacturer"
                  />
                </div>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model *
                  </label>

                  <input
                    type="text"
                    value={newEquipment.model}
                    onChange={(e) =>
                      setNewEquipment({
                        ...newEquipment,
                        model: e.target.value
                      })
                    }
                    className={inputClassName}
                    placeholder="Enter model"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SKU
                  </label>

                  <input
                    type="text"
                    value={newEquipment.sku}
                    onChange={(e) =>
                      setNewEquipment({
                        ...newEquipment,
                        sku: e.target.value
                      })
                    }
                    className={inputClassName}
                    placeholder="Enter SKU"
                  />
                </div>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Serial Number
                  </label>

                  <input
                    type="text"
                    value={newEquipment.serialNumber}
                    onChange={(e) =>
                      setNewEquipment({
                        ...newEquipment,
                        serialNumber: e.target.value
                      })
                    }
                    className={inputClassName}
                    placeholder="Enter serial number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Location
                  </label>

                  <Select
                    options={serviceLocationOptions}
                    value={serviceLocationOptions.find(
                      option =>
                        option.value === newEquipment.serviceLocation
                    )}
                    onChange={(option) =>
                      setNewEquipment({
                        ...newEquipment,
                        serviceLocation: option?.value || ''
                      })
                    }
                    styles={customStyles}
                    placeholder="Select service location"
                  />
                </div>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Installation Date
                  </label>

                  <div className='relative flex items-center'>
                    <input
                    type="text"
                    value={newEquipment.installationDate}
                    placeholder='Select Installation Date'
                    onChange={(e) =>
                      setNewEquipment({
                        ...newEquipment,
                        installationDate: e.target.value
                      })
                    }
                    className={`${inputClassName} datepicker`}
                  />
                  <CalendarIcon className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500' />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Warranty Expires
                  </label>

                  <div className='relative flex items-center'>
                    <input
                      type="text"
                      value={newEquipment.warrantyExpires}
                      placeholder='Select Warranty Expires'
                      onChange={(e) =>
                        setNewEquipment({
                          ...newEquipment,
                          warrantyExpires: e.target.value
                        })
                      }
                      className={`${inputClassName} datepicker`}
                    />
                    <CalendarIcon className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500' />
                  </div>
                  
                </div>

              </div>

              <div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Equipment Location
                </label>

                <textarea
                  value={newEquipment.equipmentLocation}
                  onChange={(e) =>
                    setNewEquipment({
                      ...newEquipment,
                      equipmentLocation: e.target.value
                    })
                  }
                  className={inputClassName}
                  rows={3}
                  placeholder="Enter equipment location"
                />

              </div>

              <div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>

                <textarea
                  value={newEquipment.notes}
                  onChange={(e) =>
                    setNewEquipment({
                      ...newEquipment,
                      notes: e.target.value
                    })
                  }
                  className={inputClassName}
                  rows={3}
                  placeholder="Enter notes"
                />

              </div>

              <div className="flex items-center">

                <input
                  type="checkbox"
                  checked={newEquipment.extendedWarranty}
                  onChange={(e) =>
                    setNewEquipment({
                      ...newEquipment,
                      extendedWarranty: e.target.checked
                    })
                  }
                  className="h-4 w-4 text-[#009efb] border-gray-300 rounded"
                />

                <label className="ml-2 block text-sm text-gray-900">
                  Extended Warranty?
                </label>

              </div>

            </div>

            <div className="flex justify-end gap-3 mt-6">

              <button
                type="button"
                onClick={() => setShowEquipmentModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleAddEquipment}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db]"
              >
                Add Equipment
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};

export default EquipmentTab;